<template>
  <div>
    <BaseCarousel
      :breakpoints="{
        1: { slidesPerView: 1.3 },
        576: { slidesPerView: 2.3 },
        992: { slidesPerView: 3.3 },
      }"
      :slides-data="testimonialVideos"
      :is-navigation-enabled="false"
      class="tw-cursor-grab active:tw-cursor-grabbing"
    >
      <template #slide="{ slideData }: { slideData: TestimonialVideosType }">
        <button
          @click="openModal(slideData.modalName)"
          class="tw-relative tw-flex tw-size-full tw-flex-col tw-justify-end tw-overflow-hidden tw-rounded-xl tw-bg-neutral-50 tw-bg-cover tw-bg-center tw-p-4 tw-text-left tw-text-white"
        >
          <img
            src="@/assets/images/video-testimonial-background.png"
            alt=""
            class="tw-absolute tw-inset-0 tw-size-full tw-object-cover"
          />

          <div class="tw-relative tw-flex tw-size-full tw-flex-col">
            <div>
              <Avatar :src="slideData.userAvatarUrl" />
              <div class="tw-mt-1 tw-text-sm tw-font-semibold">
                {{ slideData.userName }}
              </div>
              <div
                v-if="slideData.userProfessionTitle"
                class="tw-line-clamp-1 tw-text-xs tw-text-neutral-400"
              >
                {{ slideData.userProfessionTitle }}
              </div>
            </div>

            <p
              class="tw-mt-20 tw-grow tw-text-lg tw-leading-snug"
              v-html="slideData.quoteHtml"
            />

            <div class="tw-mt-6">
              <BaseIcon name="btn-play" class="tw-size-6 tw-shrink-0" />
            </div>
          </div>

          <BaseModal
            :name="slideData.modalName"
            width-class="tw-max-w-5xl tw-overflow-hidden tw-bg-transparent"
          >
            <iframe
              v-if="slideData.wistiaId"
              :src="`https://fast.wistia.com/embed/iframe/${slideData.wistiaId}`"
              allowfullscreen
              class="tw-aspect-video tw-w-full"
            />
            <iframe
              v-else
              class="tw-aspect-video tw-w-full"
              :src="`https://www.youtube.com/embed/${slideData.youtubeId}`"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            />
          </BaseModal>
        </button>
      </template>
    </BaseCarousel>
  </div>
</template>

<script setup lang="ts">
import BaseCarousel from '@/shared/components/BaseCarousel.vue'
import BaseIcon from '@/shared/components/BaseIcon.vue'
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { VideoTestimonial } from '@/modules/growthmentor/mentors/types/video-testimonial.ts'
import BaseModal from '@/shared/components/BaseModal.vue'
import { useModals } from '@/shared/composables/useModals.js'
import Avatar from '@/shared/components/avatar/Avatar.vue'

const page = usePage()
const { openModal } = useModals()

const getHighlightedQuote = (quote: string, highlightedText: string) => {
  const regex = new RegExp(highlightedText, 'g')
  const finalQuote = quote.replace(
    regex,
    `<span class="tw-font-semibold">${highlightedText}</span>`,
  )
  return `"${finalQuote}"`
}

type TestimonialVideosType = VideoTestimonial & {
  quoteHtml: string
  modalName: string
}
const testimonialVideos = computed<TestimonialVideosType[]>(() =>
  (page.props.testimonialVideos as VideoTestimonial[]).map((video) => ({
    ...video,
    quoteHtml: getHighlightedQuote(video.quote, video.highlightedText),
    modalName: `testimonial-preview-${video.id}`,
  })),
)
</script>
